import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "max-md:mx-auto max-w-[370px] w-full"
}
const _hoisted_2 = { class: "flex flex-col gap-6" }
const _hoisted_3 = { class: "text-info-900 mb-1.5" }
const _hoisted_4 = { class: "text-info-900 list-disc pl-4" }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "p-5 bg-neutral-50 border border-neutral-500 rounded-lg max-w-[450px]" }
const _hoisted_7 = { class: "text-primary-500 mb-5 text-base" }
const _hoisted_8 = { class: "flex justify-between items-center mb-5" }
const _hoisted_9 = { class: "font-bold text-base" }
const _hoisted_10 = { class: "text-base" }
const _hoisted_11 = { class: "flex justify-between items-center mb-5" }
const _hoisted_12 = { class: "font-bold text-base" }
const _hoisted_13 = { class: "text-base-200 text-base" }
const _hoisted_14 = { class: "flex justify-between items-center py-6 md:py-8 border-y border-y-neutral-900" }
const _hoisted_15 = { class: "font-bold text-base" }
const _hoisted_16 = { class: "font-bold text-xl md:text-2xl leading-xl" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { class: "text-base-200 mt-1.5 text-base" }
const _hoisted_19 = { key: 1 }
const _hoisted_20 = { class: "text-center" }
const _hoisted_21 = { class: "text-[20px] leading-lg font-bold" }
const _hoisted_22 = { class: "text-base" }
const _hoisted_23 = { class: "flex flex-col items-center list-disc text-base" }
const _hoisted_24 = { class: "m-0" }
const _hoisted_25 = { class: "m-0" }
const _hoisted_26 = { class: "m-0" }

import Card from '../layout/Card.vue';
import Button from '../base/Button.vue';
import Popup from '../layout/Popup.vue';
import { AppConfigType, SubscriptionDetailsType } from '~src/admin/types';
import { inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationStore } from '~src/admin/stores/useNotificationStore';
import { useGettext } from 'vue3-gettext';
import Axios from '~src/shared/axios';
import IconWarning from '../icons/IconWarning.vue';
import SubscriptionEnd from './SubscriptionEnd.vue';

// Translations

export default /*@__PURE__*/_defineComponent({
  __name: 'Subscription',
  setup(__props) {

const { $gettext } = useGettext();

// Show notification
const { addNotification } = useNotificationStore();

// Get plugin config
const config: AppConfigType = inject('pluginConfig');

// Router
const router = useRouter();

// Account type
const isAccountFree = inject<boolean>('isAccountFree');

// Popup control
const cancelPopup = ref<InstanceType<typeof Popup>>();

// Get subscription details
const subscriptionDetails = ref<SubscriptionDetailsType>();

// Get subscription details
onMounted(async () => {
    try {
        // Call the WordPress Rest API to get subscription details
        const response = await Axios.get(`${config.rest.endpoints.boostsite}/subscription_details`);

        // Check if the response is successful
        if (response.status !== 200 || !response.data.success) {
            throw new Error(`REST API error: ${response.status} - ${response.statusText}`);
        }

        // Update the subscription details
        if (response?.data?.data) {
			const responseData = response.data.data[0];

			// Parse the data
            subscriptionDetails.value = {
				status: responseData.status,
				planType: responseData.planType,
				price: Number(responseData.price) / 100,
				nextPayment: responseData.nextPayment ? formatDate(new Date(responseData.nextPayment * 1000)) : null,
				lastPayment: responseData.lastPayment ? formatDate(new Date(responseData.lastPayment * 1000)) : null,
				planInterval: responseData.planInterval,
			}
        }
    } catch (error) {
        console.error('Error while getting subscrpition details:', error);

        addNotification({
            message: $gettext('There was an error while getting the subscription details!'),
            type: 'danger',
        });
    }
});

// Loading
const loading = ref(false);

// Cancel subscription
const cancelSubscription = async () => {
    try {
        // Loading
        loading.value = true;

        // Call the WordPress Rest API to cancel subscription
        const response = await Axios.get(`${config.rest.endpoints.boostsite}/cancel_subscription`);

        // Check if the response is successful
        if (response.status !== 200 || !response.data.success) {
            throw new Error(`REST API error: ${response.status} - ${response.statusText}`);
        }

        // Close the popup
        cancelPopup.value.closePopup();

        // Show success notification
        addNotification({
            message: $gettext('Subscription has been successfully canceled!'),
            type: 'success',
        });

        // Refresh the page
        setTimeout(() => {
            router.go(0);
        }, 2000);
    } catch (error) {
        console.error('Error while canceling subscription:', error);

        addNotification({
            message: $gettext('There was an error while canceling the subscription!'),
            type: 'danger',
        });
    } finally {
        // Loading
        loading.value = false;
    }
};

// Format price
const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
};

// Format date
const formatDate = (date : Date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(isAccountFree))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(Card, {
            "image-url": `${_unref(config).plugin_url}/public/images/settings.png`,
            "image-alt": _unref($gettext)('Upgrade to premium plan'),
            type: "vertical"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", null, [
                  _createElementVNode("p", _hoisted_3, _toDisplayString(_unref($gettext)("With premium plan you'll get:")), 1),
                  _createElementVNode("ul", _hoisted_4, [
                    _createElementVNode("li", null, _toDisplayString(_unref($gettext)('unlimited subjects & schedules')), 1),
                    _createElementVNode("li", null, _toDisplayString(_unref($gettext)(
                                    'up to 35 articles generated per week (with option to buy additional articles when needed)'
                                )), 1),
                    _createElementVNode("li", null, _toDisplayString(_unref($gettext)('advanced scheduling')), 1),
                    _createElementVNode("li", null, _toDisplayString(_unref($gettext)('ability to add key words to every article')), 1)
                  ])
                ]),
                _createVNode(Button, {
                  text: _unref($gettext)('Upgrade'),
                  size: "large",
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (_unref(router).push('/upgrade')), ["prevent"]))
                }, null, 8, ["text"])
              ])
            ]),
            _: 1
          }, 8, ["image-url", "image-alt"])
        ]))
      : (!_unref(isAccountFree) && subscriptionDetails.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("p", _hoisted_7, _toDisplayString(subscriptionDetails.value.planType), 1),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("p", _hoisted_9, _toDisplayString(_unref($gettext)('Subtotal')), 1),
                _createElementVNode("p", _hoisted_10, _toDisplayString(formatPrice(subscriptionDetails.value.price)), 1)
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("p", _hoisted_12, _toDisplayString(_unref($gettext)('Tax')), 1),
                _createElementVNode("p", _hoisted_13, _toDisplayString(_unref($gettext)('Enter address to calculate')), 1)
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("p", _hoisted_15, _toDisplayString(_unref($gettext)('Total')), 1),
                _createElementVNode("p", _hoisted_16, _toDisplayString(formatPrice(subscriptionDetails.value.price)), 1)
              ]),
              (subscriptionDetails.value.status === 'active')
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    _createElementVNode("p", _hoisted_18, _toDisplayString(_unref($gettext)('Next payment on')) + " " + _toDisplayString(subscriptionDetails.value.nextPayment), 1)
                  ]))
                : (subscriptionDetails.value.status === 'canceled')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _createVNode(Button, {
                        class: "mt-5",
                        text: _unref($gettext)('Renew subscription'),
                        type: "primary",
                        onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
                      }, null, 8, ["text"])
                    ]))
                  : _createCommentVNode("", true)
            ]),
            (subscriptionDetails.value.status === 'active')
              ? (_openBlock(), _createBlock(Button, {
                  key: 0,
                  class: "p-0 mt-3",
                  text: _unref($gettext)('Cancel subscription'),
                  type: "secondary",
                  "has-background": false,
                  onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (cancelPopup.value.openPopup()), ["prevent"]))
                }, null, 8, ["text"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
    (!_unref(isAccountFree) && subscriptionDetails.value)
      ? (_openBlock(), _createBlock(Popup, {
          key: 2,
          ref_key: "cancelPopup",
          ref: cancelPopup,
          "cancel-button-text": _unref($gettext)('Nevermind'),
          "confirm-button-text": _unref($gettext)('Cancel subscription'),
          "confirm-button-type": "danger",
          size: "small",
          loading: loading.value,
          onPopupConfirm: cancelSubscription
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_20, [
              _createVNode(IconWarning, { class: "mb-4 mx-auto" }),
              _createElementVNode("h2", _hoisted_21, _toDisplayString(_unref($gettext)('Are you sure?')), 1),
              _createElementVNode("p", _hoisted_22, _toDisplayString(_unref($gettext)('If you cancel subscription, you will lose:')), 1),
              _createElementVNode("ul", _hoisted_23, [
                _createElementVNode("li", _hoisted_24, _toDisplayString(_unref($gettext)('ability to automate your content publishing process,')), 1),
                _createElementVNode("li", _hoisted_25, _toDisplayString(_unref(config).settings.schedules.length) + " " + _toDisplayString(_unref($gettext)('created schedules,')), 1),
                _createElementVNode("li", _hoisted_26, _toDisplayString(_unref($gettext)('35 articles generated weekly, with no hassle')), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["cancel-button-text", "confirm-button-text", "loading"]))
      : _createCommentVNode("", true),
    (subscriptionDetails.value && subscriptionDetails.value.status === 'canceled')
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 3,
          to: "#subscription-end"
        }, [
          _createVNode(SubscriptionEnd, {
            "end-time": subscriptionDetails.value.nextPayment
          }, null, 8, ["end-time"])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})